import {
  savePhaseAreaResize,
  isPhaseAreaResizeSaving,
} from "./save-phase-area-resize.js";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  ActionMenu,
  Canvas,
  CollabUnavailableNotice,
  Skeleton,
  StickyNoteLayer,
  WorkspaceControlButton,
  WorkspaceControlGroup,
  useMediaQuery,
  type CanvasConnectionState,
  type CanvasRevealRequest,
  type WidgetAskAIRequest,
} from "@playbookai/ui";
import { ArrowLeft, Settings, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { ensureCanvasShape, widgetsMap } from "@playbookai/collab-client";
import type * as Y from "yjs";
import {
  SOLVE_PHASES,
  type SolveDetail,
  type SolvePhase,
} from "@playbookai/types";
import {
  HEADER_HEIGHT,
  PHASE_META,
  SOLVE_MAX_ZOOM,
  SOLVE_MIN_ZOOM,
  SOLVE_PAN_MARGIN,
} from "./phase-metadata.js";
import {
  defaultPhaseAreaRect,
  layoutPhaseAreas,
  phaseAreaFloor,
  phasesAffectedByWidthResize,
  planPhaseAreaResize,
  revealedPhaseAreaRect,
} from "./phase-areas.js";
import {
  materializePhaseArea,
  phaseWidgetRects,
  readPhaseAreas,
} from "./phase-area-doc-shape.js";
import { usePhaseAreas } from "./usePhaseAreas.js";
import {
  PhaseSwimlanes,
  type PhaseAreaAxis,
  type PhaseAreaResizeModel,
} from "./PhaseSwimlanes.js";
import { SolveCanvasToolbar } from "./SolveCanvasToolbar.js";
import { SolveMiniMap } from "./SolveMiniMap.js";
import { SolvePhaseStrip } from "./SolvePhaseStrip.js";
import { reconcilePersistedWidgetPlacements } from "./seed-widgets.js";
import { resolveCanvasReveal } from "./reveal-widget.js";
import { ensureSolveWidgetLayout } from "./solve-widget-layout.js";
import { useApiClient } from "../api/context.js";
import {
  widgetPlacementsApi,
  type PlacementResponse,
  type WidgetResponse,
} from "../api/playbook-be-client.js";
import {
  solveWidgetsQueryKey,
  useSolveWidgets,
} from "../hooks/useSolveWidgets.js";
import { useSolve } from "../hooks/useSolve.js";
import { usePhaseLockToggle } from "../hooks/usePhaseLockToggle.js";
import { useSolvePermissions } from "../lib/useSolvePermissions.js";
import { notifyWriteError } from "../lib/notify-content-lock.js";
import { formatRelative } from "../components/status-tone.js";
import { SOLVE_RENDERABLE_WIDGET_KINDS } from "../widgets/phase-mapping.js";
import type { WorkspaceCollaborationModel } from "../collaboration/workspace-model.js";
import type { PlayerAIWidgetRevealOutcome } from "@playbookai/api-clients";
import {
  solveDetailQueryKey,
  useSolveAuthScope,
} from "../access/auth-scope.js";

// Side-effect import: register SOLVE-specific widgets (ProblemStatement) into
// the central registry before anything renders.
import "../widgets/index.js";

const DEFAULT_VIEWPORT = { x: 80, y: 80, zoom: 0.55 } as const;
const SOLVE_PHASE_SET: ReadonlySet<string> = new Set(SOLVE_PHASES);
const EMPTY_LOCKED_PHASES: readonly SolvePhase[] = [];

// A never-synced cold connect stays "connecting" while Hocuspocus keeps retrying
// a down server. After this grace period we escalate to "offline" so the user
// gets an actionable "Can't connect" + Retry instead of an endless spinner.
const COLD_CONNECT_TIMEOUT_MS = 8000;

/**
 * SOLVE canvas — pure composition over `<Canvas>` from `@playbookai/ui`.
 *
 * Storage split for widgets on this canvas:
 *   - `sticky_note` — Yjs-inline (canvas-local, CRDT-edited).
 *   - `problem_statement`, `kpi_tile` — entity references; content lives in
 *     `widget.widgets`, the canvas Y.Map carries only `{kind, widgetId, x, y, w, h}`.
 *     Editing is REST-PATCH-debounced via the resolver mounted by the shell.
 *
 * The canvas is conversation-first: nothing is ever placed automatically. On
 * open it only reconciles persisted placements back into missing canvas
 * references, so a brand-new SOLVE — and every phase revealed later — starts
 * empty until a person or Player AI deliberately creates content.
 */
export function SolveCanvas({
  solveId,
  collaboration,
  onBack,
  presenceSlot,
  peopleSlot,
  onOpenSettings,
  onOpenExport,
  onDelete,
  canAdvancePhase = false,
  advancingPhase = false,
  onAdvancePhase,
  onReturnPhase,
  completeAction,
  onRevealWidgetReady,
  deferPhaseFraming = false,
  phaseFramingRequest = 0,
}: {
  readonly solveId: string;
  readonly collaboration: WorkspaceCollaborationModel;
  readonly onBack?: () => void;
  readonly presenceSlot?: ReactNode;
  readonly peopleSlot?: ReactNode;
  readonly onOpenSettings?: () => void;
  readonly onOpenExport?: () => void;
  readonly onDelete?: () => void;
  readonly canAdvancePhase?: boolean;
  readonly advancingPhase?: boolean;
  readonly onAdvancePhase?: () => void;
  readonly onReturnPhase?: () => void;
  /** Terminal-phase completion action for the phase strip (PB-5272). */
  readonly completeAction?: ReactNode;
  /**
   * Task 9 — publishes this canvas's "bring one widget into view" resolver to
   * the page, and `null` whenever there is no canvas to navigate. The page
   * hands it on to the Player AI rail; the rail's applied-proposal rows call it.
   *
   * Published rather than driven from above because everything the answer needs
   * — the live document, the widget's current rect, and which phases this
   * reader can see — lives in here.
   */
  readonly deferPhaseFraming?: boolean;
  readonly phaseFramingRequest?: number;
  readonly onRevealWidgetReady?: (
    reveal: ((widgetId: string) => PlayerAIWidgetRevealOutcome) | null,
  ) => void;
}): ReactElement {
  if (!collaboration.document) {
    const terminal = collaboration.disposition === "terminal";
    const canRetry = !terminal && collaboration.reason !== null;
    return (
      <div className="relative h-full min-h-0">
        <CollabUnavailableNotice
          testId="solve-canvas-unavailable"
          terminal={terminal}
          title={
            terminal
              ? "Collaboration is unavailable for this SOLVE"
              : "Connecting to the SOLVE canvas"
          }
          message={
            terminal
              ? // Deliberately does NOT claim access was removed: the terminal
                // reason cannot distinguish a revocation from any other
                // authorization failure. Say what is true — the session ended.
                "This collaboration session cannot continue. Your access may be unchanged — reopen the SOLVE to try again."
              : "Your canvas will appear after access is confirmed and the latest shared state has loaded."
          }
          onRetry={canRetry ? collaboration.reconnect : undefined}
        />
        {onBack ? (
          <WorkspaceControlGroup
            placement="top-start"
            label="SOLVE navigation"
            data-geometry="solve-workspace-top-start"
            railControls
          >
            <WorkspaceControlButton
              icon={
                <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
              }
              label="Back to SOLVE overview"
              iconOnly
              shape="circle"
              onClick={onBack}
            />
          </WorkspaceControlGroup>
        ) : null}
        <WorkspaceControlGroup
          placement="top-end"
          label="SOLVE actions"
          data-geometry="solve-workspace-top-end"
          overflowSlot={
            onDelete ? (
              <ActionMenu
                triggerLabel="More SOLVE actions"
                triggerSize="sm"
                triggerShape="circle"
                items={[
                  {
                    id: "delete",
                    label: "Delete SOLVE",
                    icon: Trash2,
                    onSelect: onDelete,
                  },
                ]}
              />
            ) : null
          }
        >
          {peopleSlot}
          {onOpenSettings ? (
            <WorkspaceControlButton
              icon={<Settings className="h-4 w-4" aria-hidden />}
              label="Settings"
              alwaysShowTooltip
              onClick={onOpenSettings}
            />
          ) : null}
        </WorkspaceControlGroup>
      </div>
    );
  }
  return (
    <SolveCanvasReady
      solveId={solveId}
      collaboration={collaboration}
      doc={collaboration.document}
      {...(onBack ? { onBack } : {})}
      {...(presenceSlot ? { presenceSlot } : {})}
      {...(peopleSlot ? { peopleSlot } : {})}
      {...(onOpenSettings ? { onOpenSettings } : {})}
      {...(onOpenExport ? { onOpenExport } : {})}
      {...(onDelete ? { onDelete } : {})}
      deferPhaseFraming={deferPhaseFraming}
      phaseFramingRequest={phaseFramingRequest}
      canAdvancePhase={canAdvancePhase}
      advancingPhase={advancingPhase}
      {...(onAdvancePhase ? { onAdvancePhase } : {})}
      {...(onReturnPhase ? { onReturnPhase } : {})}
      {...(completeAction ? { completeAction } : {})}
      {...(onRevealWidgetReady ? { onRevealWidgetReady } : {})}
    />
  );
}

function SolveCanvasReady({
  solveId,
  collaboration,
  doc,
  onBack,
  presenceSlot,
  peopleSlot,
  onOpenSettings,
  onOpenExport,
  onDelete,
  canAdvancePhase,
  advancingPhase,
  onAdvancePhase,
  onReturnPhase,
  completeAction,
  onRevealWidgetReady,
  deferPhaseFraming = false,
  phaseFramingRequest = 0,
}: {
  readonly solveId: string;
  readonly collaboration: WorkspaceCollaborationModel;
  readonly doc: Y.Doc;
  readonly onBack?: () => void;
  readonly presenceSlot?: ReactNode;
  readonly peopleSlot?: ReactNode;
  readonly onOpenSettings?: () => void;
  readonly onOpenExport?: () => void;
  readonly onDelete?: () => void;
  readonly canAdvancePhase: boolean;
  readonly advancingPhase: boolean;
  readonly onAdvancePhase?: () => void;
  readonly onReturnPhase?: () => void;
  readonly completeAction?: ReactNode;
  readonly deferPhaseFraming?: boolean;
  readonly phaseFramingRequest?: number;
  readonly onRevealWidgetReady?: (
    reveal: ((widgetId: string) => PlayerAIWidgetRevealOutcome) | null,
  ) => void;
}): ReactElement {
  const qc = useQueryClient();
  const {
    isSynced,
    status,
    readOnly,
    lockState,
    liveFacilitator,
    awareness,
    reconnect,
  } = collaboration;
  const apiConfig = useApiClient();
  const scope = useSolveAuthScope(solveId);
  const detailKey = useMemo(
    () => (scope ? solveDetailQueryKey(scope, solveId) : null),
    [scope, solveId],
  );
  const appliedLiveLockStateRef = useRef(false);
  const appliedLiveFacilitatorRef = useRef(false);
  const widgetHydration = useSolveWidgets(solveId);
  const [miniMapVisible, setMiniMapVisible] = useState(false);
  const miniMapAvailable = useMediaQuery("(min-width: 1280px)");

  // Lock/role affordances (ADR-0019) derive from the capability snapshot on the
  // solve detail read (cached — SolveWorkspace already fetched it), fed through
  // the shared authorize() in useSolvePermissions. The canvas's live edit gate
  // stays `readOnly` from the collab socket above; these drive the visible
  // badge + per-phase lock affordance only.
  const solveQuery = useSolve(solveId);
  const widgetHostMetadata = useMemo(() => {
    const solve = solveQuery.data;
    if (!solve) return undefined;
    return {
      ...(solve.title ? { title: solve.title } : {}),
      ...(solve.owner?.name ? { ownerName: solve.owner.name } : {}),
      ...(solve.updatedAt
        ? { updatedAtLabel: formatRelative(solve.updatedAt) }
        : {}),
    };
  }, [solveQuery.data]);
  const perms = useSolvePermissions(solveQuery.data);
  const { pendingPhases, togglePhaseLock } = usePhaseLockToggle(solveId);
  const lockedPhases = solveQuery.data?.lockedPhases ?? EMPTY_LOCKED_PHASES;
  const activePhase = solveQuery.data?.currentPhase ?? "Specify";
  const revealedThrough = solveQuery.data?.revealedThrough ?? activePhase;
  const visiblePhases = useMemo<readonly SolvePhase[]>(() => {
    const activeIndex = Math.max(
      SOLVE_PHASES.indexOf(activePhase),
      SOLVE_PHASES.indexOf(revealedThrough),
    );
    return SOLVE_PHASES.slice(0, Math.max(0, activeIndex) + 1);
  }, [activePhase, revealedThrough]);
  const visiblePhaseSet = useMemo(
    () => new Set<SolvePhase>(visiblePhases),
    [visiblePhases],
  );
  // Each revealed phase owns its own rect (`phase-areas.ts`). With nothing
  // stored this collapses to the historical five fixed 816px lanes, so the
  // extent below is byte-identical to the old `phases.length * LANE_WIDTH`.
  const { layout: phaseAreaLayout, extent: phaseAreaBounds } = usePhaseAreas(
    doc,
    visiblePhases,
  );
  const visibleCanvasWidth = phaseAreaBounds.w;
  // May this caller create canvas content right now? Owner/Facilitator always;
  // a Collaborator only on an unlocked canvas; a Viewer never. Gates the manual
  // add toolbar and the legacy layout migration below — otherwise a lock-bound
  // user's write is rejected server-side (CONTENT_LOCKED). Owner writes even on
  // the default-locked canvas because they are lock-exempt.
  const canAddContent =
    perms.isLockExempt ||
    (perms.canEditAtAll && !(solveQuery.data?.canvasLocked ?? true));
  // The legacy migration can touch every phase, not just the visible lane.
  const canNormalizeLayout =
    canAddContent && SOLVE_PHASES.every((phase) => perms.canEditPhase(phase));

  // Resizing a phase area is a canvas write, so it obeys the same per-phase
  // gate every widget edit does (ADR-0019): the live socket `readOnly` plus
  // `canEditPhase`, which folds in role, the canvas lock and that phase's own
  // lock. Pre-sync counts as not-editable for the same reason schema-init does
  // — a write before the server state lands produces local structs a read-only
  // connection is forbidden from sending.
  //
  // Note the server has no per-phase granularity on the Yjs socket yet (that
  // deferral is recorded in `services/collab/src/hocuspocus/socket-editability.ts`),
  // so for a pure-Yjs write this gate is the phase-lock enforcement — the same
  // posture sticky notes and VSM's container geometry already have.
  // Widening an area PUSHES every later area right and carries their widgets
  // (user decision, 2026-09-13), so the width axis touches those phases too and
  // must be authorized for all of them — the same deny-on-any rule the REST
  // canvas gate applies through `additionalPhases` when a widget move spans two
  // phases. Height moves nothing, so it needs only its own phase.
  const canResizePhaseArea = useCallback(
    (phase: SolvePhase, axis: PhaseAreaAxis): boolean => {
      if (
        isPhaseAreaResizeSaving(doc) ||
        !isSynced ||
        readOnly ||
        !perms.canEditPhase(phase)
      )
        return false;
      if (axis === "h") return true;
      return phasesAffectedByWidthResize(visiblePhases, phase).every((pushed) =>
        perms.canEditPhase(pushed),
      );
    },
    [doc, isSynced, readOnly, perms, visiblePhases],
  );

  const placementsByWidgetId = useMemo(() => {
    const byWidgetId = new Map<string, PlacementResponse>();
    for (const placement of widgetHydration.data ?? []) {
      byWidgetId.set(placement.widget.id, placement);
    }
    return byWidgetId;
  }, [widgetHydration.data]);

  const cachePlacement = useCallback(
    (placement: PlacementResponse): void => {
      qc.setQueryData<WidgetResponse>(
        ["widget", placement.widget.id],
        placement.widget,
      );
      qc.setQueryData<PlacementResponse[]>(
        solveWidgetsQueryKey(solveId),
        (current = []) => {
          const withoutExisting = current.filter(
            (p) => p.id !== placement.id && p.widget.id !== placement.widget.id,
          );
          return [...withoutExisting, placement].sort((a, b) =>
            a.createdAt.localeCompare(b.createdAt),
          );
        },
      );
    },
    [qc, solveId],
  );

  const resolveWidgetReadOnly = useCallback(
    (ymap: Y.Map<unknown>): boolean => {
      const phase = ymap.get("phase");
      return isSolvePhase(phase) && !perms.canEditPhase(phase);
    },
    [perms],
  );

  const resolveWidgetVisibility = useCallback(
    (ymap: Y.Map<unknown>): boolean => {
      const phase = ymap.get("phase");
      if (isSolvePhase(phase)) return visiblePhaseSet.has(phase);
      return numberFromYMap(ymap, "x", 0) < visibleCanvasWidth;
    },
    [visibleCanvasWidth, visiblePhaseSet],
  );

  /**
   * Task 9 — "View on canvas", from the rail's side of the shell seam.
   *
   * A request is a `{widgetId, nonce}` the viewport controller serves once; the
   * nonce is what makes pressing the same row twice move the canvas twice. The
   * outcome is returned SYNCHRONOUSLY so the row can say what happened instead
   * of looking dead, and nothing here selects, opens or writes: a reveal is a
   * viewport change plus a temporary outline, so an open editor, a canvas
   * selection and the caller's typing focus all survive it.
   */
  const [revealRequest, setRevealRequest] = useState<
    CanvasRevealRequest | undefined
  >(undefined);
  const revealNonce = useRef(0);
  const revealWidget = useCallback(
    (widgetId: string): PlayerAIWidgetRevealOutcome => {
      if (!isSynced || !widgetHydration.isSuccess || !solveQuery.data)
        return { status: "unavailable" };
      const resolution = resolveCanvasReveal(
        doc,
        widgetId,
        resolveWidgetVisibility,
      );
      if (resolution.status !== "revealed") return resolution;
      revealNonce.current += 1;
      setRevealRequest({
        widgetId: resolution.canvasWidgetId,
        nonce: revealNonce.current,
      });
      return { status: "revealed" };
    },
    [
      doc,
      isSynced,
      widgetHydration.isSuccess,
      widgetHydration.data,
      solveQuery.data,
      resolveWidgetVisibility,
    ],
  );

  const resolveNoteVisibility = useCallback(
    (note: Y.Map<unknown>): boolean =>
      numberFromYMap(note, "x", 0) < visibleCanvasWidth,
    [visibleCanvasWidth],
  );

  const resolveWidgetLockState = useCallback(
    (ymap: Y.Map<unknown>) => {
      const phase = ymap.get("phase");
      if (!isSolvePhase(phase) || !lockedPhases.includes(phase)) return null;
      return {
        label: "Phase locked",
        dimmed: resolveWidgetReadOnly(ymap),
      };
    },
    [lockedPhases, resolveWidgetReadOnly],
  );

  const removeCachedPlacement = useCallback(
    (placementId: string | null, widgetId: string | null): void => {
      qc.setQueryData<PlacementResponse[]>(
        solveWidgetsQueryKey(solveId),
        (current = []) =>
          current.filter(
            (p) =>
              (placementId === null || p.id !== placementId) &&
              (widgetId === null || p.widget.id !== widgetId),
          ),
      );
      if (widgetId) {
        qc.removeQueries({ queryKey: ["widget", widgetId], exact: true });
      }
    },
    [qc, solveId],
  );

  // Schema-init is idempotent, but it is still a WRITE, so it obeys the same
  // live permission gate as every editor — matching VsmCanvas and both Hoshin
  // canvases. Pre-sync counts as not-editable: seeding an empty doc before the
  // server state lands produces local structs, and a read-only connection is
  // forbidden from sending document content, so the server closes it. That is
  // what stranded a member demoted to Viewer mid-session while their client was
  // correctly reconnecting at the lower role.
  useEffect(() => {
    if (readOnly || !isSynced) return;
    ensureCanvasShape(doc);
  }, [doc, readOnly, isSynced]);

  // SQL placements are a recovery index for canvas references. Reconcile only
  // after both sources have hydrated, and never alter an existing Yjs ref.
  useEffect(() => {
    if (!isSynced || !widgetHydration.isSuccess || widgetHydration.isError) {
      return;
    }
    reconcilePersistedWidgetPlacements(doc, widgetHydration.data ?? []);
  }, [
    doc,
    isSynced,
    widgetHydration.data,
    widgetHydration.isError,
    widgetHydration.isSuccess,
  ]);

  useEffect(() => {
    onRevealWidgetReady?.(revealWidget);
    // Withdrawn when this canvas goes away, so a rail left open over a
    // disconnected or unmounted workspace answers "unavailable" rather than
    // navigating a document nobody is looking at any more.
    return () => onRevealWidgetReady?.(null);
  }, [onRevealWidgetReady, revealWidget]);

  useEffect(() => {
    if (!lockState) {
      if (appliedLiveLockStateRef.current) {
        appliedLiveLockStateRef.current = false;
        if (detailKey) {
          void qc.invalidateQueries({ queryKey: detailKey, exact: true });
        }
      }
      return;
    }
    appliedLiveLockStateRef.current = true;
    if (!detailKey) return;
    qc.setQueryData<SolveDetail>(detailKey, (current) =>
      current
        ? {
            ...current,
            canvasLocked: lockState.canvasLocked,
            lockedPhases: lockState.lockedPhases,
          }
        : current,
    );
  }, [detailKey, lockState, qc, solveId]);

  // Live Facilitator grant/revoke (ADR-0019) — patch the cached capability
  // snapshot so the lock/role affordances (controls, badge, canAddContent)
  // re-derive without a reload. Mirrors the lock_state effect above: a
  // facilitator change carries no lock_state, and its readOnly flip alone does
  // not surface the lock controls (they key off isFacilitator, not readOnly).
  useEffect(() => {
    if (liveFacilitator === null) {
      if (appliedLiveFacilitatorRef.current) {
        appliedLiveFacilitatorRef.current = false;
        if (detailKey) {
          void qc.invalidateQueries({ queryKey: detailKey, exact: true });
        }
      }
      return;
    }
    appliedLiveFacilitatorRef.current = true;
    if (!detailKey) return;
    qc.setQueryData<SolveDetail>(detailKey, (current) =>
      current ? { ...current, isFacilitator: liveFacilitator } : current,
    );
  }, [detailKey, liveFacilitator, qc, solveId]);

  // Normalize legacy default-roster geometry once the provider has synced.
  // SOLVE no longer seeds any widget: a new canvas — and every phase revealed
  // after it — stays empty until a person or Player AI deliberately creates
  // grounded content. Canvases seeded before that change keep their widgets,
  // so this one-time layout migration still has to run for them.
  useEffect(() => {
    // Same write gate the seeding path used: a lock-bound Collaborator / Viewer
    // must not push canvas writes the server would refuse.
    if (
      !isSynced ||
      !widgetHydration.isSuccess ||
      widgetHydration.isError ||
      !canNormalizeLayout
    ) {
      return;
    }
    void ensureSolveWidgetLayout(doc);
  }, [
    doc,
    isSynced,
    widgetHydration.isError,
    widgetHydration.isSuccess,
    canNormalizeLayout,
  ]);

  // Place an area the moment its phase is REVEALED — to the right of the
  // current area's actual bounds plus a consistent gap, so a revealed area can
  // never land on top of one that was widened. Reveal itself is unchanged: it
  // still follows `currentPhase`, which only the existing authorized
  // advance-phase path moves.
  //
  // Deliberately keyed to an OBSERVED transition rather than to "this phase has
  // no entry yet". Materializing on open would shift every pre-existing canvas
  // right by the gap while its widgets stayed put; keyed to the transition, an
  // untouched canvas is never written to at all. A client that misses the
  // transition (offline, or not permitted to write) still renders the area
  // without an overlap — `layoutPhaseAreas` pushes it flush against its
  // predecessor — it just doesn't get the gap until a writer places it.
  const revealedAreaCountRef = useRef<number | null>(null);
  useEffect(() => {
    // Until the detail read lands, `activePhase` is the "Specify" fallback, so
    // the revealed prefix is a guess. Take the baseline from the first render
    // that actually knows the phase, or opening a SOLVE already past Specify
    // would read as a burst of reveals.
    if (!solveQuery.data) return;
    const revealed = visiblePhases.length;
    const previous = revealedAreaCountRef.current;
    revealedAreaCountRef.current = revealed;
    if (previous === null || revealed <= previous) return;
    if (!isSynced || readOnly) return;
    for (let index = previous; index < revealed; index += 1) {
      const phase = visiblePhases[index];
      const precedingPhase = visiblePhases[index - 1];
      if (!phase || !precedingPhase || !perms.canEditPhase(phase)) continue;
      // Re-read per step: placing one area moves the edge the next one is
      // measured from.
      const preceding = layoutPhaseAreas(
        visiblePhases,
        readPhaseAreas(doc),
      ).get(precedingPhase);
      if (!preceding) continue;
      materializePhaseArea(doc, phase, revealedPhaseAreaRect(preceding));
    }
  }, [doc, isSynced, readOnly, perms, solveQuery.data, visiblePhases]);

  const syncWidgetPosition = useCallback(
    async (canvasWidgetId: string, ymap: Y.Map<unknown>): Promise<void> => {
      const widgetId = ymap.get("widgetId");
      if (typeof widgetId !== "string" || widgetId.length === 0) return;

      const attemptedPosition = positionFromYMap(ymap);
      const ymapPhase = ymap.get("phase");
      try {
        const placement = await widgetPlacementsApi.upsert(apiConfig, {
          widgetId,
          surface: "solve_canvas",
          surfaceId: solveId,
          position: attemptedPosition,
          // Cross-phase moves re-send the phase — the placement upsert is
          // the single authoritative phase source (ADR-0019). A move to a
          // phase-agnostic spot sends an explicit null so the persisted
          // phase (and its lock binding) doesn't go stale.
          ...(typeof ymapPhase === "string"
            ? { phase: ymapPhase === "any" ? null : ymapPhase }
            : {}),
        });
        cachePlacement(placement);
        doc.transact(() => {
          const current = widgetsMap(doc).get(canvasWidgetId);
          current?.set("placementId", placement.id);
        });
      } catch (err) {
        // A locked phase (or any error) rejects the move. Revert the optimistic
        // Yjs x/y to the last server-persisted position so the widget doesn't
        // linger where the server refused it (and that phantom position doesn't
        // keep broadcasting to peers), then surface it — never silent.
        // Read the synchronous query cache, not the render that queued this save.
        const savedPlacement =
          qc
            .getQueryData<PlacementResponse[]>(solveWidgetsQueryKey(solveId))
            ?.find((placement) => placement.widget.id === widgetId) ??
          placementsByWidgetId.get(widgetId);
        const persisted = savedPlacement?.position;
        const currentPosition = positionFromYMap(ymap);
        const unchangedSinceRequest =
          ymap.get("phase") === ymapPhase &&
          ["x", "y", "w", "h"].every(
            (key) => currentPosition[key] === attemptedPosition[key],
          );
        if (
          unchangedSinceRequest &&
          persisted &&
          typeof persisted === "object"
        ) {
          doc.transact(() => {
            for (const key of ["x", "y", "w", "h"] as const) {
              const v = (persisted as Record<string, unknown>)[key];
              if (typeof v === "number") ymap.set(key, v);
            }
            ymap.set("phase", savedPlacement?.phase ?? "any");
          });
        }
        notifyWriteError(err, qc, "Move not saved");
      }
    },
    [apiConfig, cachePlacement, doc, solveId, qc, placementsByWidgetId],
  );

  // Resize carries use one audited placement batch; individual drags retain
  // their ordinary save path above.
  const phaseAreaResize = useMemo<PhaseAreaResizeModel>(() => {
    const floorFor = (phase: SolvePhase) => {
      const rect = phaseAreaLayout.get(phase) ?? defaultPhaseAreaRect(phase);
      const occupants = phaseWidgetRects(doc, phase);
      const floor = phaseAreaFloor(rect, occupants);
      return { ...floor, widgetCount: occupants.length };
    };
    return {
      canResize: canResizePhaseArea,
      floorFor,
      commit: (phase, size) => {
        const rect = phaseAreaLayout.get(phase);
        if (!rect) return;
        // Re-plan on the write path. The gesture is already bounded, but the
        // pointer and keyboard routes must not be able to disagree about where
        // an area stops, and a peer may have moved a widget since the gesture
        // began.
        const plan = planPhaseAreaResize(
          phaseAreaLayout,
          visiblePhases,
          phase,
          phaseWidgetRects(doc, phase),
          size,
        );
        if (!plan) return;
        // Authorize the axis the plan actually exercises: a width change that
        // pushes later phases needs every one of them, and re-checking here
        // (not only at the handle) keeps a revoked grant from landing a write
        // the gesture started while it was still allowed.
        const axis: PhaseAreaAxis = plan.carry.dx === 0 ? "h" : "w";
        if (!canResizePhaseArea(phase, axis)) return;
        void savePhaseAreaResize(doc, plan, solveId, apiConfig).then(
          (placements) => {
            try {
              placements.forEach(cachePlacement);
            } catch (error) {
              notifyWriteError(error, qc, "Resize saved; refresh needed");
            }
          },
          (error) => notifyWriteError(error, qc, "Resize not saved"),
        );
      },
    };
  }, [
    canResizePhaseArea,
    doc,
    phaseAreaLayout,
    apiConfig,
    cachePlacement,
    solveId,
    qc,
    visiblePhases,
  ]);

  const removeWidget = useCallback(
    (canvasWidgetId: string, ymap: Y.Map<unknown>): void => {
      const widgetIdRaw = ymap.get("widgetId");
      const widgetId = typeof widgetIdRaw === "string" ? widgetIdRaw : null;
      const placementIdRaw = ymap.get("placementId");
      const placementId =
        typeof placementIdRaw === "string"
          ? placementIdRaw
          : widgetId
            ? (placementsByWidgetId.get(widgetId)?.id ?? null)
            : null;

      void (async () => {
        try {
          if (placementId) {
            await widgetPlacementsApi.delete(apiConfig, placementId);
          }
          doc.transact(() => {
            widgetsMap(doc).delete(canvasWidgetId);
          });
          removeCachedPlacement(placementId, widgetId);
        } catch (err) {
          // A locked canvas/phase rejects the placement delete for a lock-bound
          // Collaborator — surface the friendly lock toast; any other failure
          // gets a generic error toast + log (never silent).
          notifyWriteError(err, qc, "Couldn't remove widget");
        }
      })();
    },
    [apiConfig, doc, qc, placementsByWidgetId, removeCachedPlacement],
  );

  const askPlayerAI = useCallback(
    (request: WidgetAskAIRequest): void => {
      if (typeof window === "undefined") return;
      window.dispatchEvent(
        new CustomEvent("playerai:open", {
          detail: {
            solveId,
            widgetKind: request.kind,
            widgetId: request.entityId,
            canvasWidgetId: request.canvasWidgetId,
            widgetLabel: request.label,
            prompt: request.prompt,
          },
        }),
      );
    },
    [solveId],
  );

  const publishPlayerAISelection = useCallback(
    (_canvasWidgetId: string | null, ymap: Y.Map<unknown> | null): void => {
      if (typeof window === "undefined") return;
      const widgetId = ymap?.get("widgetId");
      const widgetKind = ymap?.get("kind");
      window.dispatchEvent(
        new CustomEvent("playerai:context", {
          detail: {
            solveId,
            ...(typeof widgetId === "string" && widgetId ? { widgetId } : {}),
            ...(typeof widgetKind === "string" && widgetKind
              ? { widgetKind }
              : {}),
          },
        }),
      );
    },
    [solveId],
  );

  // Remember whether this doc has ever synced. Yjs resets `isSynced` to false on
  // disconnect, so without this flag a mid-session drop would read as the
  // first-load "Connecting…" rather than "Reconnecting…".
  const hasEverSynced = useRef(false);
  useEffect(() => {
    if (isSynced) hasEverSynced.current = true;
  }, [isSynced]);

  // Escalate a never-synced cold connect to "offline" after a grace period.
  // Hocuspocus holds status "connecting" while it retries a down server, so
  // without this the banner would spin on "Connecting…" indefinitely with no
  // Retry. The timer starts once per disconnected episode — a ref guard keeps
  // the connecting↔disconnected backoff flicker from resetting it — and is
  // cleared the moment we connect (or on unmount).
  const [coldConnectFailed, setColdConnectFailed] = useState(false);
  const coldTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const clearColdTimer = (): void => {
      if (coldTimer.current !== null) {
        clearTimeout(coldTimer.current);
        coldTimer.current = null;
      }
    };
    if (status === "connected") {
      clearColdTimer();
      setColdConnectFailed(false);
      return;
    }
    // A mid-session drop is handled by `hasEverSynced` → "reconnecting".
    if (hasEverSynced.current) {
      clearColdTimer();
      return;
    }
    if (coldTimer.current === null && !coldConnectFailed) {
      coldTimer.current = setTimeout(() => {
        coldTimer.current = null;
        setColdConnectFailed(true);
      }, COLD_CONNECT_TIMEOUT_MS);
    }
    // No cleanup here: the timer must persist across status flicker.
  }, [status, coldConnectFailed]);
  useEffect(
    () => () => {
      // Null the ref too — under StrictMode's mount→unmount→remount the
      // remount's start-check (`coldTimer.current === null`) must see a clear
      // slot, or it would treat the cleared timer as still pending and never
      // start a new one (the spinner would never escalate).
      if (coldTimer.current !== null) {
        clearTimeout(coldTimer.current);
        coldTimer.current = null;
      }
    },
    [],
  );

  // Map the collab (canvas) socket state onto the DS banner vocabulary:
  //   connected             → hidden
  //   dropped after a sync  → "reconnecting" (transient; offers Retry)
  //   never synced yet      → "connecting" through the cold-connect window
  //                           (including transient "disconnected" backoff),
  //                           escalating to "offline" only once the grace timer
  //                           fires (`coldConnectFailed`). Driving offline off
  //                           the timer rather than a raw "disconnected" keeps
  //                           the grace period intact instead of flashing
  //                           "Can't connect" on the first failed attempt.
  // Auth/membership failures collapse to "disconnected" in collab-client today;
  // indistinguishable from a transient drop, they surface as "offline" after the
  // same grace period (the DS `unavailable` terminal state is reserved for a
  // future widened hook — see CanvasConnectionState).
  const connectionState: CanvasConnectionState =
    status === "connected"
      ? "connected"
      : hasEverSynced.current
        ? "reconnecting"
        : coldConnectFailed
          ? "offline"
          : "connecting";

  if (widgetHydration.isLoading && !widgetHydration.data) {
    return (
      <div aria-busy="true" className="flex-1 min-h-0 bg-pb-page p-4">
        {/* The five tinted lanes the board will become, so the loading frame
            already has the canvas's shape rather than a centred sentence. */}
        <div aria-hidden className="grid h-full min-h-0 grid-cols-5 gap-2">
          {SOLVE_PHASES.map((phase) => (
            <div
              key={phase}
              className="rounded-lg p-3"
              style={{ backgroundColor: PHASE_META[phase].accentBg }}
            >
              <Skeleton shape="circle" className="h-7 w-7" />
              <Skeleton className="mt-3 h-3 w-2/3" />
              <Skeleton className="mt-6 h-24" />
            </div>
          ))}
        </div>
        <span className="sr-only">Loading canvas widgets…</span>
      </div>
    );
  }

  return (
    <Canvas
      doc={doc}
      dimensions={{ w: visibleCanvasWidth, h: phaseAreaBounds.h }}
      widgetKinds={SOLVE_RENDERABLE_WIDGET_KINDS}
      solveId={solveId}
      widgetHostMetadata={widgetHostMetadata}
      connectionState={connectionState}
      awareness={awareness}
      readOnly={readOnly}
      resolveWidgetReadOnly={resolveWidgetReadOnly}
      resolveWidgetVisibility={resolveWidgetVisibility}
      resolveWidgetLockState={resolveWidgetLockState}
      revealRequest={revealRequest}
      onReconnect={() => {
        // Fire-and-forget: HocuspocusProvider.connect() returns a Promise that
        // can reject. Swallow the rejection (the banner already reflects the
        // connection state) so a failed manual reconnect isn't an unhandled
        // rejection. Promise.resolve() guards a void return too.
        void Promise.resolve(reconnect()).catch((err) => {
          console.warn(
            "[SolveCanvas] manual reconnect failed:",
            err instanceof Error ? err.message : err,
          );
        });
      }}
      reservedTop={HEADER_HEIGHT}
      background={
        <PhaseSwimlanes
          phases={visiblePhases}
          areas={phaseAreaLayout}
          areaResize={phaseAreaResize}
          lockedPhases={lockedPhases}
          canManageLocks={perms.canManagePhaseLock}
          pendingPhases={pendingPhases}
          onToggleLock={togglePhaseLock}
        />
      }
      viewportOverlaySlot={
        <StickyNoteLayer
          doc={doc}
          canvasW={visibleCanvasWidth}
          canvasH={phaseAreaBounds.h}
          readOnly={readOnly}
          resolveNoteVisibility={resolveNoteVisibility}
        />
      }
      hudSlot={
        <>
          <SolvePhaseStrip
            phases={visiblePhases}
            areas={phaseAreaLayout}
            currentPhase={activePhase}
            deferPhaseFraming={deferPhaseFraming}
            phaseFramingRequest={phaseFramingRequest}
            {...(onBack ? { onBack } : {})}
            canAdvance={canAdvancePhase && activePhase !== "Ensure"}
            advancing={advancingPhase}
            {...(onReturnPhase ? { onReturn: onReturnPhase } : {})}
            {...(onAdvancePhase ? { onAdvance: onAdvancePhase } : {})}
            {...(activePhase === "Ensure" && completeAction
              ? { completeAction }
              : {})}
          />
          <SolveCanvasToolbar
            doc={doc}
            solveId={solveId}
            activePhase={activePhase}
            canvasWidth={visibleCanvasWidth}
            canvasHeight={phaseAreaBounds.h}
            onWidgetPlaced={cachePlacement}
            areaGrowth={{
              visiblePhases,
              canEditPhase: (phase) =>
                isSynced && !readOnly && perms.canEditPhase(phase),
              onPlacementsSaved: (placements) =>
                placements.forEach(cachePlacement),
            }}
            miniMapVisible={miniMapVisible}
            miniMapAvailable={miniMapAvailable}
            onToggleMiniMap={() => setMiniMapVisible((visible) => !visible)}
            {...(presenceSlot ? { presenceSlot } : {})}
            {...(peopleSlot ? { peopleSlot } : {})}
            {...(onOpenSettings ? { onOpenSettings } : {})}
            {...(onOpenExport ? { onOpenExport } : {})}
            {...(onDelete ? { onDelete } : {})}
            canAddWidgets={canAddContent}
            canAddToPhase={(phase) =>
              phase === "any" ? canAddContent : perms.canEditPhase(phase)
            }
          />
          {miniMapAvailable && miniMapVisible ? (
            <SolveMiniMap
              doc={doc}
              phases={visiblePhases}
              areas={phaseAreaLayout}
              resolveWidgetVisibility={resolveWidgetVisibility}
              resolveNoteVisibility={resolveNoteVisibility}
            />
          ) : null}
        </>
      }
      onWidgetRemove={removeWidget}
      onWidgetAskAI={askPlayerAI}
      onWidgetSelectionChange={publishPlayerAISelection}
      onWidgetDragEnd={syncWidgetPosition}
      defaultViewport={DEFAULT_VIEWPORT}
      minZoom={SOLVE_MIN_ZOOM}
      maxZoom={SOLVE_MAX_ZOOM}
      panMargin={SOLVE_PAN_MARGIN}
    />
  );
}

function isSolvePhase(value: unknown): value is SolvePhase {
  return typeof value === "string" && SOLVE_PHASE_SET.has(value);
}

function positionFromYMap(ymap: Y.Map<unknown>): Record<string, unknown> {
  const position: Record<string, unknown> = {
    x: numberFromYMap(ymap, "x", 0),
    y: numberFromYMap(ymap, "y", 0),
    w: numberFromYMap(ymap, "w", 220),
    h: numberFromYMap(ymap, "h", 150),
  };
  const rosterKey = ymap.get("rosterKey");
  if (ymap.get("isDefaultRoster") === true && typeof rosterKey === "string") {
    position["isDefaultRoster"] = true;
    position["rosterKey"] = rosterKey;
  }
  return position;
}

function numberFromYMap(
  ymap: Y.Map<unknown>,
  key: string,
  fallback: number,
): number {
  const value = ymap.get(key);
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
